/**
 * Format Audit Message Utilities
 *
 * Transform audit log data into human-readable French messages.
 * @see US-19a.28
 */

import type { AuditAction, ActorType, AuditLogListItem } from '@bstock/shared';

/**
 * Mapping des actions en francais
 */
export const ACTION_LABELS: Record<AuditAction, string> = {
  CREATE: 'a cree',
  UPDATE: 'a modifie',
  DELETE: 'a supprime',
  VALIDATE: 'a valide',
  CANCEL: 'a annule',
  LOGIN: 's\'est connecte',
  LOGOUT: 's\'est deconnecte',
};

/**
 * Mapping des types d'entites en francais
 */
export const ENTITY_TYPE_LABELS: Record<string, string> = {
  RAW_PRODUCT: 'le produit brut',
  POS_PRODUCT: 'le produit fini',
  INVENTORY: 'l\'inventaire',
  TRANSFER: 'le transfert',
  GOODS_RECEIPT: 'le bon de reception',
  PURCHASE_ORDER: 'le bon de commande',
  SUPPLIER: 'le fournisseur',
  RECIPE: 'la recette',
  USER: 'l\'utilisateur',
  CATEGORY: 'la categorie',
  UNIT: 'l\'unite',
  PACKAGING: 'le conditionnement',
  BRAND: 'la marque',
  CATALOG_ITEM: 'l\'article catalogue',
  LOSS: 'la perte',
  STOCK_MOVEMENT: 'le mouvement de stock',
  POS_SALE: 'la vente POS',
  POS_SYNC: 'la synchronisation POS',
};

/**
 * Labels pour les types d'acteurs
 */
export const ACTOR_TYPE_LABELS: Record<ActorType, string> = {
  USER: 'Utilisateur',
  SYSTEM: 'Systeme',
  CRON: 'Tache planifiee',
  API: 'API',
};

/**
 * Get the actor display name
 * Priorite: Prenom Nom > Email > Type d'acteur
 */
export function getActorName(log: AuditLogListItem): string {
  if (log.actorType === 'SYSTEM') {
    return 'Systeme';
  }
  if (log.actorType === 'CRON') {
    return 'Tache planifiee';
  }
  if (log.actorType === 'API') {
    return 'API';
  }
  // Afficher le nom complet si disponible
  if (log.actor?.firstName && log.actor?.lastName) {
    return `${log.actor.firstName} ${log.actor.lastName}`;
  }
  return log.actorEmail || 'Utilisateur inconnu';
}

/**
 * Get the entity type label in French
 */
export function getEntityTypeLabel(entityType: string): string {
  return ENTITY_TYPE_LABELS[entityType] || entityType.toLowerCase().replace(/_/g, ' ');
}

/**
 * Get the action label in French
 */
export function getActionLabel(action: AuditAction): string {
  return ACTION_LABELS[action] || action.toLowerCase();
}

/**
 * Format a complete audit message
 *
 * @example
 * "jean.dupont@example.com a cree le produit Tomate"
 * "Systeme a synchronise la synchronisation POS"
 */
export function formatAuditMessage(log: AuditLogListItem): string {
  const actor = getActorName(log);
  const action = getActionLabel(log.action);
  const entityType = getEntityTypeLabel(log.entityType);
  const entityName = log.entityName ? ` "${log.entityName}"` : '';

  // Handle special cases for LOGIN/LOGOUT
  if (log.action === 'LOGIN' || log.action === 'LOGOUT') {
    return `${actor} ${action}`;
  }

  return `${actor} ${action} ${entityType}${entityName}`;
}

/**
 * Format relative time (e.g., "il y a 5 minutes")
 * Note: This is a utility function. For timezone-aware formatting in components,
 * use the useFormatDate hook from @/hooks.
 *
 * @param dateString - ISO date string
 * @param formatDateShortFn - Optional formatter function from useFormatDate for fallback
 */
export function formatRelativeTime(
  dateString: string,
  formatDateShortFn?: (date: string) => string
): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'A l\'instant';
  }
  if (diffMinutes < 60) {
    return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
  }
  if (diffHours < 24) {
    return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
  }
  if (diffDays < 7) {
    return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
  }

  // For older dates, use timezone-aware formatting if available
  if (formatDateShortFn) {
    return formatDateShortFn(dateString);
  }

  // Fallback to static formatting (only used if no formatter provided)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Format absolute date and time
 * Note: For timezone-aware formatting in components, use the useFormatDate hook from @/hooks.
 *
 * @param dateString - ISO date string
 * @param formatDateTimeFn - Optional formatter function from useFormatDate
 */
export function formatAbsoluteDateTime(
  dateString: string,
  formatDateTimeFn?: (date: string) => string
): string {
  if (formatDateTimeFn) {
    return formatDateTimeFn(dateString);
  }

  // Fallback to static formatting (only used if no formatter provided)
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
